import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import s from "./WhyWeAsk.module.css";

export interface WhyWeAskProps {
  /** Blank-line-separated paragraphs; a paragraph whose every line starts
      with "- " renders as a bulleted list. */
  children: string;
  /** Overrides the trigger's link text -- the questionnaire's copy varies
      this per question (e.g. "Mikä lasketaan runsaaksi?" rather than a
      single generic "Miksi kysymme?" everywhere). Defaults to onboarding's
      original fixed text so existing callers are unaffected. */
  linkText?: string;
}

/**
 * The "Miksi kysymme tätä?" sheet the Glide app carries under its intimate
 * questions. It is what makes a question about pregnancy or menopause
 * answerable rather than intimidating, so it stays — as an opt-in link
 * rather than always-on body copy, to keep each step to one question.
 */
export function WhyWeAsk({ children, linkText }: WhyWeAskProps) {
  const { t } = useTranslation("client");

  return (
    <Dialog>
      <DialogTrigger className={s.trigger}>{linkText ?? t("onboarding.why", "Miksi kysymme?")}</DialogTrigger>
      <DialogContent className={s.content}>
        <DialogTitle className={s.title}>
          {t("onboarding.whyDialogTitle", "Miksi kysymme tätä?")}
        </DialogTitle>
        <div className={s.body}>
          {children.split(/\n\s*\n/).map((block, i) => {
            const lines = block.trim().split("\n");
            return lines.every((line) => line.startsWith("- ")) ? (
              <ul key={i} className={s.list}>
                {lines.map((line, j) => (
                  <Text as="li" size="md" key={j}>
                    {line.slice(2)}
                  </Text>
                ))}
              </ul>
            ) : (
              <Text as="p" size="md" key={i}>
                {block.trim()}
              </Text>
            );
          })}
        </div>
        <DialogClose className={s.close}>{t("onboarding.whyClose", "Sulje")}</DialogClose>
      </DialogContent>
    </Dialog>
  );
}
